import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { Frame } from 'scenejs';

import { EditorContext } from '../../../index';
import StoryboardContext from 'components/Storyboard/context';

import { toggleEditMode, updateWidgetEditorState, updateWidgetProperty } from 'actions/editor';
import { useChildrenWidgetSelected, useContainerSelector, usePageWidgetSelector, useWidgetChildrenSelector, useWidgetSelected, useWidgetSelector } from 'components/Editor/hooks/useEditorSession';
import { Property } from '../../../components/properties';
import { Background } from 'components/Editor/components/properties/background';
import { Container } from '../../core/container';
import { BackgroundView } from '../../background';
import Tooltip from 'components/Tooltip';

import styles from './style.module.scss';
import { getMediaInfo } from 'actions/pageHealth';

const IMAGE_CDN_ENDPOINT = process.env.REACT_APP_IMAGE_CDN;

const SectionColumnWidgetView = function SectionColumnWidget({ style, widgetProps, widgetRef, children, columnGap }) {
  const ref = widgetRef;
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const parent = _.get(widgetProps, 'parent');
  const layout = _.get(widgetProps, 'properties.desktop.layout');
  const background = _.get(widgetProps, 'properties.desktop.background');

  const selected = useWidgetSelected(editor, id);
  const parentSelected = useWidgetSelected(editor, parent);
  const siblingSelection = useChildrenWidgetSelected(editor, parent);
  const childrenSelection = useChildrenWidgetSelected(editor, id);

  const container = _.get(widgetProps, `properties.${breakpoint}.container`);
  const selectedParent = useContainerSelector(editor, _.get(widgetProps, 'uid'));
  const stretched = _.get(selectedParent, 'properties.desktop.stretched', false);
  const isLocked = _.get(widgetProps, `properties.desktop.locked`)

  useEffect(() => {
    if (selected && isLocked) {
      dispatch(updateWidgetEditorState(editor, widgetProps.parent, {
        manageColumns: false
      }));
    }
  }, [selected, isLocked])
  
  var widgetStyle = {
    ...style,
    transform: 'none',
    ...getStyles(widgetProps, breakpoint, columnGap)
  }

  return (
    <>
      <Container widgetProps={widgetProps} widgetRef={widgetRef}>
        <div
          id={id}
          ref={ref}
          className={cx(styles.column, 'widget', {
            [styles.selected]: selected,
            [styles.parentSelected]: parentSelected,
            [styles.partialSelection]: childrenSelection.selected
          })}
          style={widgetStyle}
          data-widget-id={id}
          data-container={true}
          data-widget-type={type}
          data-selectable={parentSelected || siblingSelection.selected}
        >
          <BackgroundView background={background} partialSelection={childrenSelection.selected}/>

          <div className={styles.inner}>
            {children}
          </div>

          <VisibleArea id={id} container={container} editorContext={editorContext} stretched={stretched} breakpoint={breakpoint} childrenSelection={childrenSelection}/>
        </div>
      </Container>
    </>
  );
};

const VisibleArea = ({ id, container, editorContext, stretched, childrenSelection, breakpoint }) => {
  const [state, setState] = useState(null);


  const visibleWidth = _.get(container, `minWidth`);
  const alignment = _.get(container, `alignment`) || 'center';
  const showGridCondition = childrenSelection?.selected && stretched

  var multiplier = 0;
  if (alignment == 'center') {
    multiplier = 0.5;
  } else if (alignment == 'right') {
    multiplier = 1;
  }

  var areaStyles = {
    width: `${visibleWidth}px`,
    marginLeft: `calc((100% - ${visibleWidth}px) * ${multiplier})`
  }

  useEffect(() => {
    editorContext.editorSession.eventBus.on("onParentHover", (data) => {
      setState(data);
    })

    editorContext.editorSession.eventBus.on("onParentChange", ({ widget, parent }) => {
      setState(null);
    })
  }, []);

  return (
    <>
      <div className={cx(styles.boundary, styles.left)} style={{
        left: `calc((100% - ${visibleWidth}px) * ${multiplier})`,
      }} />
      {alignment!=='left' &&
        <div
          id={showGridCondition && breakpoint === 'desktop' && `gridOutLeft`}
          className={cx(styles.gridOutCon, {[styles.gridOut]: _.get(state, 'gridOut')})}
          style={{width: `calc((100% - ${visibleWidth}px) * ${multiplier})`, left: 0}}>
        </div>
      }
      <div className={cx(styles.boundary, styles.right)} style={{
        right: `calc((100% - ${visibleWidth}px) * ${1 - multiplier})`,
      }} />
      {alignment!=='right' && 
        <div
          id={showGridCondition && breakpoint === 'desktop' && `gridOutRight`}
          className={cx(styles.gridOutCon, {[styles.gridOut]: _.get(state, 'gridOut')})}
          style={{width: `calc((100% - ${visibleWidth}px) * ${1 - multiplier})`, right:0}}>
        </div>
      }  
      <div id={`${id}_visible_area`} className={cx(styles.visibleArea, 'cs_visible_area__content')} style={areaStyles}>
      </div>
    </>
  );
}

const SectionColumnWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();
  const [view, setView] = useState(null);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const storyboardContext = React.useContext(StoryboardContext);

  const id = _.get(props, 'uid');
  const background = _.get(props, 'properties.desktop.background');
  const selectedParent = useContainerSelector(editorSession, id);
  const stretched = _.get(selectedParent, 'properties.desktop.stretched', false);

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const handleManageColumns = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);

    dispatch(updateWidgetEditorState(editorSession, props.parent, {
      manageColumns: true
    }));
  }

  const closePopup = () => {
    setView(null);
    dispatch(toggleEditMode(editorSession, props.uid, false));
    dispatch(updateWidgetEditorState(editorSession, props.parent, {
      manageColumns: false
    }));
  }

  return (
    <>
      <Property.Action className={styles.textButton} onClick={() => changeView('background')}>
        Change Background
      </Property.Action>
      <Property.Divider />
      <Property.Action className={styles.textButton} onClick={handleManageColumns}>
        Manage Columns
      </Property.Action>
      <Property.Divider />
      
      {!['INTERACTIVE_CONTENT', 'SPATIAL'].includes(storyboardContext.type) && stretched &&
        <Property.Action className={styles.iconButton} tooltip='Layout' onClick={() => changeView('alignment')}>
          <i className={cx("cleverstory-layout")} />
        </Property.Action>
      }
      

      {view === 'background' &&
        <Background.Dialog id={id}
          value={background}
          widgetProps={props}
          onChange={(value) => {
            dispatch(updateWidgetProperty(editorSession, id, 'desktop', {
              background: value
            }));
          }}
          onCancel={() => {
            closePopup();
          }}
        />
      }

      {view === 'alignment' &&
        <ColumnLayoutEditor
          id={id}
          parent={props.parent}
          editor={editorSession}
          widget={props}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

const SectionColumnWidget = React.forwardRef((props, ref) => {
  return <SectionColumnWidgetView {...props} />
});

const ColumnLayoutEditor = ({ id, parent, widget, position, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));
  const properties = _.get(widget, `properties.desktop`, {});

  const container = _.get(properties, `container`);
  const alignment = _.get(properties, 'container.alignment', 'center');

  const handlePropertyChange = (value) => {
    dispatch(updateWidgetProperty(editorSession, id, 'desktop', value));
  }

  const handleAlignmentChange = (value) => {
    handlePropertyChange({
      container: {
        ...container,
        alignment: value
      }
    });
  }

  return (
    <Property.Dialog className={styles.fontDialog}
      width={246}
      target={`#${id}`}
      title={'Manage Column'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property name="Align content to:" className={styles.columnAlignmentProperty}>
          <div className={styles.columnAlignment}>
            <div className={styles.options}>
              <Tooltip placement="top" content="Left Align">
                <button className={cx(styles.alignment,
                  {
                    [styles.active]: alignment === 'left'
                  })}
                  onClick={handleAlignmentChange.bind(this, 'left')}
                >
                  <i className={cx('cleverstory-align-left')}></i>
                </button>
              </Tooltip>
              <Tooltip placement="top" content="Center Align">
                <button className={cx(styles.alignment,
                  {
                    [styles.active]: alignment === 'center'
                  })}
                  onClick={handleAlignmentChange.bind(this, 'center')}
                >
                  <i className={cx('cleverstory-align-center')}></i>
                </button>
              </Tooltip>
              <Tooltip placement="top" content="Right Align">
                <button className={cx(styles.alignment,
                  {
                    [styles.active]: alignment === 'right'
                  })}
                  onClick={handleAlignmentChange.bind(this, 'right')}
                >
                  <i className={cx('cleverstory-align-right')}></i>
                </button>
              </Tooltip>
            </div>
          </div>
        </Property>
      </Property.Body>
    </Property.Dialog>
  );
}

function getStyles(widget, breakpoint, columnGap) {
  var styles = {};

  if (breakpoint === 'desktop') {
    const width = parseFloat(_.get(widget, `properties.${breakpoint}.layout.width`, 100));

    styles = {
      ...styles,
      maxWidth: `calc(${width}% - ${columnGap}px)`,
      height: '100%'
    }
  }

  return styles;
}

SectionColumnWidget.actions = (editorSession, breakpoint, props) => {
  if (breakpoint === 'desktop') {
    return <SectionColumnWidgetDesktopAction {...props} />
  }

  return false;
}

SectionColumnWidget.processRequest = (widget) => {
  var background = _.get(widget, 'properties.desktop.background');

  if (!background) {
    return widget;
  }

  var media = _.get(background, 'media');
  if (media) {
    background = {
      ...background,
      media: media.id
    }
  }

  return {
    ...widget,
    properties: {
      ...widget.properties,
      desktop: {
        ...widget.properties.desktop,
        background: background
      }
    }
  }
}

SectionColumnWidget.sizeConfig = async (widget, mediaCache) => {
  const url = _.get(widget, 'properties.desktop.background.media.url', '');
  if (!url) {
    return {
      size: 0,
      type: 'image'
    };
  }
  return {
    size:  await getMediaInfo(`${IMAGE_CDN_ENDPOINT}/${url}`, mediaCache) || 0, // MB
    type: 'image'
  };
}

SectionColumnWidget.moveableConfig = (editorSession, widget, breakpoint) => {
  const desktop = breakpoint === 'desktop';

  return {
    draggable: false,
    resizable: !desktop,
    rotatable: false,
    renderDirections: !desktop ? ['n', 's'] : [],
    onResize: (widget, layout, breakpoint) => {
      const frame = new Frame(layout);
      frame.set('transform', 'translate', '0px, 0px');

      if(!layout.height) {
        frame.remove('height')
      }

      return frame.get();
    }
  }
}


export { SectionColumnWidget };